<!--
  StoreCheckoutInfo.vue

  Checkout Fields section of the store editor (PR C2 of the
  Store Editor Redesign closing chain, 2026-06-16).

  Three stacked EditorCards:

    1. Identity     -- First / Last / Email / Phone toggles. Phone is
                       the only new toggle; binds to a new persisted
                       boolean column ``campaign.field_phone_enabled``
                       (see migration ``route_fork_schema_030``).

    2. Shipping     -- ONE radio group mapped to ``stores.shipping_mode``
                       (store ship-to, Jake 2026-10-19):
                          a. Ship to shopper's address (collect at checkout)
                          b. Ship to company address   (this store's own
                             ship-to address, edited here through the
                             validated ShippingAddressEditor, with a
                             "Use the client's company address" autofill)
                          c. Ship to a specific department (only while
                             departments are in use; a picker of the
                             store's departments with address status)
                       With no shopper address the order ships to the
                       department, else the store address, else the
                       client's company address, else the distributor
                       address (api/src/services/store_ship_to.py). With
                       departments in use the Department field is shown
                       required in modes a and b; mode c does not ask.
                       Legacy Location/Manager stores derive to
                       ship_to_company when no shipping_mode is present.

    3. Custom fields -- existing custom text + dropdown chrome, unchanged.

  Department auto-enable: when ``campaign.client_departments`` is non-empty
  (PR E: typed client-department mapping; replaces the dropped legacy
  ``stores.departments`` JSON column), ``fieldDepartmentEnabled`` auto-flips
  to true so the "Department" checkout field continues to flow to the admin
  order dialogs. Phase 3 removed the dead configurer TOGGLE (a no-op that
  pushed an unread flag) and replaced it with a read-only department-shipping
  note; the auto-enable projection itself is retained.

  Field-lifecycle gating remains via the shared
  ``computeDisableFlags`` helper -- only ``complete`` and the
  in-session "new order added" flag lock the form, per the relaxation
  documented in ``disableFlags.js``.

  Shipping-readiness truthfulness (card trello.com/c/NCJnYsw5): the
  department/company shipping note used to assert the fallback chain
  ("...falling back to the company address...") works UNCONDITIONALLY,
  which contradicted the Review & Launch readiness card whenever the
  chain actually resolved to nothing (e.g. an unvalidated company/
  department address). This section now fetches the SAME
  ``GET /api/stores/<id>/readiness/`` payload the readiness card reads
  (via the shared ``useStoreReadinessStore`` Pinia store) and keys the
  note + a standalone warning off its ``shipping_unresolvable`` blocker,
  so the two surfaces read from one source of truth and cannot disagree.
-->
<template>
  <div
    class="checkout-fields-layout"
    data-testid="checkout-fields-layout"
  >
    <!-- Sticky toolbar: the checkout preview is a popover (same right-hand
         drawer look as the Storefront preview), so the form gets the full
         width. The button stays reachable while the long form scrolls. -->
    <div class="checkout-preview-toolbar">
      <button
        ref="previewOpenBtn"
        type="button"
        class="checkout-preview-open-btn"
        data-testid="checkout-preview-open-btn"
        aria-haspopup="dialog"
        :aria-expanded="previewOpen ? 'true' : 'false'"
        @click="openPreview"
      >
        <span class="tf-icon" aria-hidden="true">visibility</span>
        <span>Preview checkout</span>
      </button>
    </div>

    <v-form
      ref="form"
      class="checkout-info-form"
      :class="['checkout-fields-layout__main']"
      v-on:submit.prevent=""
    >
      <!-- A store save the server REFUSED for this section's slice (Bug
           Intake #450). The section has already gone back to what is
           saved; this says why, here rather than in the editor-wide
           banner. Cleared when the owner picks a shipping option again. -->
      <v-alert
        v-if="checkoutSaveRefusal"
        type="warning"
        variant="tonal"
        density="comfortable"
        class="mb-4"
        role="alert"
        data-testid="ct-checkout-save-refused"
      >
        {{ checkoutSaveRefusal }} The settings below are the saved ones.
      </v-alert>
      <!-- Card 1: Identity -->
      <EditorCard
        title="Identity"
        description="Personal info shoppers enter at checkout."
      >
        <!--
          G2 fix (task #110): use a flex container with auto-width
          children instead of a v-row/v-col grid. The 4-up grid was
          the root cause of letter-by-letter label wrapping: in the
          two-column store-editor layout the left column is ~360px,
          each ``md="3"`` col was ~90px, the switch control alone
          consumed ~50px and the label was crushed into a 40px box --
          forcing "First Name" / "Last Name" to wrap a character at
          a time.

          A flex-wrap container lets each switch take its intrinsic
          width (switch + full label, no wrap), and wraps WHOLE
          toggles onto a new line when horizontal space runs out --
          which is the desired behavior. The ``.identity-row`` class
          name is kept so the existing layout assertion (task #110)
          still anchors to a known surface.
        -->
        <div class="identity-row" data-testid="identity-row">
          <v-switch
            class="text-left identity-row__switch"
            v-model="fieldFirstNameEnabled"
            label="First Name"
            :disabled="disableForCampaignLifecycle"
            data-testid="ct-checkout-first"
          />
          <v-switch
            class="text-left identity-row__switch"
            v-model="fieldLastNameEnabled"
            label="Last Name"
            :disabled="disableForCampaignLifecycle"
            data-testid="ct-checkout-last"
          />
          <!--
            PR-G (2026-06-22): the Email toggle now renders regardless of
            ``campaign.email_shopper``. Pre-PR-G the switch was hidden
            when shopper notifications were enabled, which made it
            impossible for an Owner to disable the Email field without
            also disabling Notifications. Email always shows; the
            Notifications surface can mention "requires email" inline if
            that constraint matters operationally.
          -->
          <v-switch
            class="text-left identity-row__switch"
            v-model="fieldCompanyEmailEnabled"
            label="Email"
            :disabled="disableForCampaignLifecycle"
            @update:modelValue="changeCompanyEmailEnabled"
            data-testid="ct-checkout-email"
          />
          <v-switch
            class="text-left identity-row__switch"
            v-model="fieldPhoneEnabled"
            label="Phone"
            :disabled="disableForCampaignLifecycle"
            @update:modelValue="changePhoneEnabled"
            data-testid="ct-checkout-phone"
          />
        </div>
      </EditorCard>

      <!--
        PR-G (2026-06-22): Custom fields now renders BEFORE Shipping. The
        former order (Identity / Shipping / Custom fields) put a niche
        flow (Pickup-to-locations) ahead of the more commonly edited
        Custom fields card. Both cards retain their full prior behavior;
        only the source order in this <v-form> changed.
      -->
      <!-- Card 2: Custom fields -->
      <EditorCard
        title="Custom fields"
        description="Optional checkout fields specific to this store."
      >
        <v-switch
          class="text-left"
          v-model="fieldCustomEnabled"
          label="Add a Custom Field"
          :disabled="disableForCampaignLifecycle"
          data-testid="ct-checkout-identifier"
        />
        <v-row>
          <v-col cols="12" sm="6">
            <div v-if="fieldCustomEnabled">
              <div class="md-subheader">Custom Text Fields</div>
              <v-form v-model="customFieldsValid" ref="customCheckoutForm">
                <div
                  v-for="(field, index) in fieldCustom"
                  :key="'custom_field_' + index"
                >
                  <div class="d-flex align-baseline">
                    <v-col cols="10">
                      <v-text-field
                        v-model="fieldCustom[index]"
                        :rules="[validateNotEmpty]"
                        @update:modelValue="changeCustomCheckoutField"
                        label="Field Label"
                        :disabled="disableForCampaignLifecycle"
                      />
                    </v-col>
                    <v-col cols="2">
                      <v-btn
                        @click="removeCustomField(index)"
                        :disabled="disableForCampaignLifecycle"
                        variant="flat"
                        data-testid="ct-checkout-identifier-delete"
                        icon
                      >
                        <v-icon icon="delete" />
                      </v-btn>
                    </v-col>
                  </div>
                </div>
              </v-form>
              <v-btn
                @click="addCustomField"
                :disabled="disableForCampaignLifecycle || !customFieldsValid"
                data-testid="ct-checkout-identifier-add"
                class="add-department-btn extended-fab mt-2"
                rounded="pill"
                size="large"
                color="primary"
              >
                <v-icon color="white" class="mr-2 extended-fab-icon" icon="add" />
                <v-spacer />
                <div style="color: white">Add Field</div>
              </v-btn>
            </div>
          </v-col>
          <v-spacer />
          <v-col cols="12" sm="6">
            <div v-if="fieldCustomEnabled">
              <div class="md-subheader">Custom Dropdown Fields</div>
              <v-form
                v-model="customDropdownsValid"
                ref="customDropdownCheckoutForm"
              >
                <div
                  v-for="(field, index) in customDropdowns"
                  :key="'custom_dropdown_' + index"
                >
                  <div class="d-flex flex-wrap align-baseline">
                    <v-col cols="10">
                      <v-text-field
                        :disabled="disableForCampaignLifecycle"
                        v-model="customDropdowns[index]['name']"
                        :rules="[validateNotEmpty]"
                        @update:modelValue="changeCustomCheckoutField"
                        label="Dropdown Label"
                      />
                    </v-col>
                    <v-col cols="2">
                      <v-btn
                        @click="removeCustomDropdownField(index)"
                        :disabled="disableForCampaignLifecycle"
                        variant="flat"
                        data-testid="ct-checkout-identifier-delete"
                        icon
                      >
                        <v-icon icon="delete" />
                      </v-btn>
                    </v-col>
                    <v-col cols="10">
                      <v-combobox
                        class="mt-0"
                        :disabled="campaign.complete"
                        :items="customDropdowns[index]['options']"
                        v-model="customDropdowns[index]['options']"
                        hide-selected
                        :rules="
                          customDropdownOptionRules(
                            customDropdowns[index]['options']
                          )
                        "
                        label="Add Dropdown Options"
                        multiple
                        persistent-hint
                        chips
                        data-testid="dropdown-options"
                        @update:modelValue="changeCustomCheckoutFieldDropdown"
                      >
                        <template v-slot:chip="{ item, props: chipProps }">
                          <v-chip
                            v-bind="chipProps"
                            :disabled="
                              optionHasOrders(item.value) ||
                                disableForCampaignLifecycle
                            "
                          >
                            {{ item.title }}
                            <v-spacer />
                            <v-btn
                              :disabled="
                                optionHasOrders(item.value) ||
                                  disableForCampaignLifecycle
                              "
                              icon
                              size="x-small"
                              class="circle"
                              variant="flat"
                              @click="removeCustomDropdownFieldOption(index, item.value)"
                              data-testid="delete-dropdown-option"
                              ><v-icon class="circle" size="small" icon="close" /></v-btn
                            >
                          </v-chip>
                        </template>
                        <template v-slot:no-data>
                          <p class="ma-2">
                            No results matching. Press <kbd>enter</kbd> to create a new one
                          </p>
                        </template>
                      </v-combobox>
                    </v-col>
                  </div>
                </div>
              </v-form>
              <v-btn
                @click="addCustomDropdown"
                :disabled="disableForCampaignLifecycle || !customDropdownsValid"
                data-testid="ct-checkout-identifier-add"
                class="add-department-btn extended-fab mt-2"
                rounded="pill"
                size="large"
                color="primary"
              >
                <v-icon color="white" class="mr-2 extended-fab-icon" icon="add" />
                <v-spacer />
                <div style="color: white">Add Dropdown</div>
              </v-btn>
            </div>
          </v-col>
          <v-spacer />
        </v-row>
      </EditorCard>

      <!-- Card 3: Shipping (moved to the bottom in PR-G — the
           Pickup-to-locations flow is niche, so it sits below the more
           commonly edited Identity + Custom fields). -->
      <EditorCard
        title="Shipping"
        description="How orders get to the recipient."
      >
        <!-- Store ship-to modes (Jake, 2026-10-19). The mode is persisted in
             ``stores.shipping_mode`` and drives the checkout resolver
             (api/src/services/store_ship_to.py). When the shopper does not
             enter an address the order ships to: the department (when one
             applies and has a validated address), else this store's
             ship-to address, else the client's company address, else the
             distributor address. -->
        <p
          class="ct-checkout-shipping-rule-note"
          data-testid="ct-checkout-shipping-rule-note"
        >
          Either collect a shipping address at checkout, or ship every order
          to an address you set: this store's company address<span
            v-if="departmentsInUse || shipMode === 'ship_to_department'"
          >, or one department's address</span>. Company and department
          addresses are kept on the client page.
        </p>

        <v-radio-group
          v-model="shipMode"
          :disabled="disableForCampaignLifecycle"
          data-testid="ct-checkout-ship-mode"
          @update:modelValue="onShipModeChange"
        >
          <!-- The radio root spans the row, so a click on its centre lands
               on empty space and selects nothing. The labels carry their
               own testids: scenarios click the label, which selects the
               radio the way a user's click does. -->
          <v-radio
            value="ship_to_shopper_address"
            data-testid="ct-checkout-ship-mode-shopper"
            :disabled="disableForCampaignLifecycle"
          >
            <template #label>
              <span data-testid="ct-checkout-ship-mode-shopper-label">
                Ship to shopper's address
              </span>
            </template>
          </v-radio>
          <v-radio
            value="ship_to_company"
            data-testid="ct-checkout-ship-mode-company"
            :disabled="disableForCampaignLifecycle"
          >
            <template #label>
              <span data-testid="ct-checkout-ship-mode-company-label">
                Ship to company address
              </span>
            </template>
          </v-radio>
          <!-- Offered only while departments are in use (feature ON and the
               store's client has departments mapped to it). A store already
               saved in this mode keeps the option visible so the screen
               shows what is saved. -->
          <v-radio
            v-if="departmentsInUse || shipMode === 'ship_to_department'"
            value="ship_to_department"
            data-testid="ct-checkout-ship-mode-department"
            :disabled="disableForCampaignLifecycle"
          >
            <template #label>
              <span data-testid="ct-checkout-ship-mode-department-label">
                Ship to a specific department
              </span>
            </template>
          </v-radio>
        </v-radio-group>

        <!-- Department mode: which department every order ships to. -->
        <div
          v-if="shipMode === 'ship_to_department'"
          class="ship-dept-pick"
          data-testid="ct-checkout-ship-department"
        >
          <v-select
            v-if="departmentsInUse"
            v-model="shipToDepartmentId"
            :items="departmentPickerItems"
            item-title="title"
            item-value="value"
            label="Department"
            :disabled="disableForCampaignLifecycle"
            data-testid="ct-checkout-ship-department-picker"
            @update:modelValue="onShipDepartmentChange"
          />
          <p
            v-if="departmentsInUse && shipToDepartmentId == null"
            class="ship-dept-pick__note ship-dept-block__note--warning"
            data-testid="ct-checkout-ship-department-unset"
          >
            Pick the department these orders ship to. Until you do, orders
            ship as they would for the company address.
          </p>
          <p
            v-if="!departmentsInUse"
            class="ship-dept-pick__note ship-dept-block__note--warning"
            data-testid="ct-checkout-ship-department-inactive"
          >
            This store doesn't use departments right now, so orders ship to
            the store address below, then the client's company address, then
            the distributor address.
          </p>
        </div>

        <!-- This store's own ship-to address (company and department
             modes). Saved through the validated path (ShipEngine), never
             through the store autosave. In department mode it is the
             fallback for a department with no validated address. -->
        <div
          v-if="shipMode === 'ship_to_company' || shipMode === 'ship_to_department'"
          class="ship-company-block"
          data-testid="ct-checkout-company-address"
        >
          <div
            class="ship-store-address"
            data-testid="ct-checkout-store-address"
          >
            <ShippingAddressEditor
              v-if="storeId"
              ref="storeAddressEditor"
              :endpoint="`/stores/${storeId}/shipping-address`"
              label="Store ship-to address"
              :disabled="disableForCampaignLifecycle"
            />
            <p
              v-else
              class="ship-company-block__empty"
              data-testid="ct-checkout-store-address-unsaved"
            >
              Save the store first, then set its ship-to address here.
            </p>
            <div
              v-if="storeId && !disableForCampaignLifecycle && campaign && campaign.client_id != null"
              class="ship-store-address__autofill"
            >
              <v-btn
                variant="outlined"
                size="small"
                :loading="copyingClientAddress"
                data-testid="ct-checkout-store-address-use-client"
                @click="useClientCompanyAddress"
              >
                Use the client's company address
              </v-btn>
              <span
                v-if="clientAddressMissing"
                class="ship-company-block__empty"
                data-testid="ct-checkout-store-address-use-client-empty"
              >
                The client has no company address to copy yet.
              </span>
            </div>
            <p
              class="ship-store-address__hint"
              data-testid="ct-checkout-store-address-fallback-hint"
            >
              {{ storeAddressFallbackHint }}
            </p>
          </div>

          <div class="ship-company-block__head">
            Client's company address
          </div>
          <p
            v-if="companyAddressLines.length"
            class="ship-company-block__addr"
            data-testid="ct-checkout-company-address-lines"
          >
            <span
              v-for="(line, i) in companyAddressLines"
              :key="'company-addr-' + i"
            >{{ line }}<br /></span>
          </p>
          <p
            v-else
            class="ship-company-block__empty"
            data-testid="ct-checkout-company-address-empty"
          >
            No company shipping address set yet.
          </p>
          <router-link
            v-if="clientPageLink"
            :to="clientPageLink"
            class="ship-company-block__link"
            data-testid="ct-checkout-company-address-edit-link"
          >
            Edit company address on the client page
          </router-link>
        </div>

        <!-- The Department checkout field. With departments in use the
             shopper MUST pick a department in the shopper and company
             modes (it decides the budget the order is charged to, and in
             company mode where it ships), so the field is shown on and
             cannot be turned off. In department mode the store already
             knows the department and the shopper is not asked. -->
        <template v-if="departmentsInUse">
          <template v-if="shipMode !== 'ship_to_department'">
            <v-switch
              class="text-left"
              :model-value="true"
              label="Department (required)"
              disabled
              data-testid="ct-checkout-department-required"
            />
            <p
              class="ct-checkout-department-required-note"
              data-testid="ct-checkout-department-required-reason"
            >
              {{ departmentRequiredReason }}
            </p>
          </template>
          <p
            v-else
            class="ct-checkout-department-required-note"
            data-testid="ct-checkout-department-not-asked"
          >
            Shoppers aren't asked for a department: every order is charged to
            and ships to {{ shipToDepartmentName || "the department you pick" }}.
          </p>
        </template>

        <!-- Department-addresses indicator (card trello.com/c/NCJnYsw5).
             The copy depends on ``shippingResolvable`` -- the SAME signal
             (``GET /api/stores/<id>/readiness/``) the Review & Launch
             readiness card reads -- so the two surfaces cannot disagree. -->
        <div
          v-if="departmentsInUse"
          class="ship-dept-block"
          data-testid="ct-checkout-department-shipping-note"
        >
          <p v-if="shippingResolvable !== false" class="ship-dept-block__note">
            {{ departmentShippingNote }}
          </p>
          <p v-else class="ship-dept-block__note ship-dept-block__note--warning">
            This store uses departments, but the fallback chain isn't
            working right now -- orders have nowhere to ship.
          </p>
          <router-link
            v-if="clientPageLink"
            :to="clientPageLink"
            class="ship-dept-block__link"
            data-testid="ct-checkout-department-edit-link"
          >
            Edit department addresses on the client page
          </router-link>
        </div>

        <!-- Shipping-unresolvable warning. Renders whenever the fetched
             readiness payload carries the ``shipping_unresolvable``
             blocker, regardless of ship mode -- this is the same
             condition that hard-blocks launch on Review & Launch. Names
             the actual reason (an existing address that isn't validated,
             per the backend message) rather than repeating the generic
             "add an address" guidance the distributor may have already
             satisfied. -->
        <div
          v-if="shippingResolvable === false"
          class="ship-unresolvable-warning"
          data-testid="ct-checkout-shipping-unresolvable-warning"
        >
          <p class="ship-unresolvable-warning__note">
            {{ shippingUnresolvableMessage }}
          </p>
        </div>
      </EditorCard>
    </v-form>

    <!-- Checkout preview popover (non-modal: no scrim, the form stays
         editable and the pane updates live). Pure-presentational pane; receives
         campaign + the in-component field flags as a merged object so
         toggles in Identity reflect immediately, before any persistence
         round-trip. Mounted only while open. -->
    <template v-if="previewOpen">
      <aside
        class="checkout-preview-drawer"
        data-testid="checkout-preview-drawer"
        role="dialog"
        aria-label="Checkout preview"
      >
        <div class="checkout-preview-drawer__head">
          <div>
            <div class="checkout-preview-drawer__ttl">Checkout preview</div>
            <div class="checkout-preview-drawer__meta">
              What shoppers enter at checkout. Updates as you edit.
            </div>
          </div>
          <button
            ref="previewCloseBtn"
            type="button"
            class="checkout-preview-drawer__close"
            data-testid="checkout-preview-close-btn"
            title="Close preview"
            aria-label="Close preview"
            @click="closePreview"
          >
            <span class="tf-icon" aria-hidden="true">close</span>
          </button>
        </div>
        <div class="checkout-preview-drawer__body">
          <CheckoutPreviewPane
            :campaign="previewCampaign"
            data-testid="checkout-preview-pane-host"
          />
        </div>
      </aside>
    </template>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { mapGetters } from "vuex";
import TabError from "./TabError.vue";
import EditorCard from "./components/EditorCard.vue";
import CheckoutPreviewPane from "./components/CheckoutPreviewPane.vue";
import ShippingAddressEditor from "@/pages/distributor/clients/ShippingAddressEditor.vue";
import rules from "@/mixins/rules";
import api from "@/services/api";
import { computeDisableFlags } from "./disableFlags.js";
import { useStoreReadinessStore } from "@/stores/storeReadiness";
import { useEnableDepartments } from "@/composables/useEnableDepartments";

// Wire code the backend uses for the shipping-unresolvable readiness
// blocker (state_transition_guard.py::SHIPPING_UNRESOLVABLE_CODE). Kept
// as a local constant rather than importing across the frontend/backend
// boundary -- the two are already coupled by the wire contract.
const SHIPPING_UNRESOLVABLE_CODE = "shipping_unresolvable";

// Radio value (``shipMode``) <-> persisted ``stores.shipping_mode``
// (api/src/services/store_ship_to.py::SHIPPING_MODES).
const SHIP_MODE_TO_SHIPPING_MODE = {
  ship_to_shopper_address: "shopper_address",
  ship_to_company: "company",
  ship_to_department: "department",
};
const SHIPPING_MODE_TO_SHIP_MODE = Object.fromEntries(
  Object.entries(SHIP_MODE_TO_SHIPPING_MODE).map(([k, v]) => [v, k])
);

/**
 * Derive the single ``shipMode`` value from the legacy ship flags.
 *
 * Phase 3 (shipping-address redesign): the store editor now offers only
 * two modes -- collect-at-checkout OR ship-to-company. The legacy
 * ``ship_to_locations`` mode is retired (its editor was dead) and
 * ``ship_to_manager`` is replaced by ``ship_to_company``. Both retired
 * legacy states (Location / Manager) migrate to ``ship_to_company``: a
 * store that used to send everything to a manager or to a pre-defined
 * location now resolves to the client's company/department address via the
 * Phase 2 resolver.
 *
 *   1. ship_to_shopper_address  (shopper enters their own address)
 *   2. ship_to_company          (resolves to company/department address)
 *
 * If none of the flags are set AND nothing is saved (brand-new store), the
 * default is ``ship_to_shopper_address``. If a NON-EMPTY list is saved
 * (``saved``) with no ship flag, the store is saved as NOT collecting an
 * address -- exactly how the launch check reads it
 * (state_transition_guard.compute_store_shipping_readiness) -- so the radio
 * shows ``ship_to_company``. What the screen shows must be what is saved
 * (Bug Backlog #340).
 */
export function deriveShipMode({ shopper, location, manager, saved = false }) {
  if (shopper) return "ship_to_shopper_address";
  // Legacy Location / Manager stores collapse into the company mode -- the
  // fallback owner the resolver now uses.
  if (location || manager) return "ship_to_company";
  if (saved) return "ship_to_company";
  return "ship_to_shopper_address";
}

export default {
  setup() {
    // Departments feature gate (SA-global), read through the ONE shared
    // composable. Consumed by ``departmentsInUse`` below -- deliberately
    // ONE consumption point in this file rather than a check per surface,
    // so the department-shipping note and the "Department" checkout-field
    // auto-enable cannot disagree about whether departments are in play.
    const { enableDepartments } = useEnableDepartments();
    return { enableDepartments };
  },

  props: {
    campaign: Object,
    editMode: Boolean,
  },

  components: {
    TabError,
    EditorCard,
    CheckoutPreviewPane,
    ShippingAddressEditor,
  },

  mixins: [rules],

  data() {
    return {
      fieldFirstNameEnabled: !this.editMode,
      fieldLastNameEnabled: !this.editMode,
      // PR-G (2026-06-22): new stores default Email to ON, matching the
      // First / Last defaults. Existing stores still hydrate from the
      // persisted ``checkout_fields`` list (see fillPreliminaryData).
      fieldCompanyEmailEnabled: !this.editMode,
      fieldPhoneEnabled: false,
      fieldDepartmentEnabled: false,
      fieldLocationEnabled: false,
      fieldShopperLocationEnabled: !this.editMode,
      fieldManagerEnabled: false,
      fieldCustomEnabled: false,

      // Checkout preview popover (opened from the sticky toolbar button).
      previewOpen: false,

      fieldCustom: [],
      customDropdowns: [],
      customFieldsValid: true,
      customDropdownsValid: true,

      // Shipping mode is the single source of truth for the C2 radio
      // group. ``fieldShopperLocationEnabled`` / ``fieldLocationEnabled``
      // / ``fieldManagerEnabled`` are derived from it on save (see
      // ``syncShipFlagsFromMode`` + ``updateCampaign``). The default
      // mirrors the pre-C2 new-store default (shopper address).
      shipMode: "ship_to_shopper_address",
      // The server's reason for refusing this section's last save, shown
      // inline (adoptSavedCheckout). Empty when nothing was refused.
      checkoutSaveRefusal: "",
      // Department mode: the department every order ships to and is
      // charged to (``stores.ship_to_department_id``).
      shipToDepartmentId: null,
      // Address status per department id, from the client's department
      // list (GET /clients/<id>/departments/), for the picker labels.
      departmentAddressStatus: {},
      // "Use the client's company address" autofill state.
      copyingClientAddress: false,
      clientAddressMissing: false,
      validateKey: 0,

      closedErrorNotification: false,

      usedCustomDropdownOptions: [],

      // Phase 3: the client's company shipping address, fetched read-only
      // for the "Ship to company address" mode display. Populated by
      // ``loadCompanyAddress`` when the store has a client_id. The address
      // itself is edited on the client page (Phase 1 editor); this is a
      // read-only mirror.
      companyAddress: null,

      // Card trello.com/c/NCJnYsw5: mirrors the Review & Launch readiness
      // payload (GET /api/stores/<id>/readiness/) so the department/company
      // shipping note can tell the truth about whether the fallback chain
      // actually resolves, instead of asserting it unconditionally. Fetched
      // via the shared ``useStoreReadinessStore`` Pinia store -- the SAME
      // store StoreReadinessCard reads -- so this section and Review &
      // Launch can never disagree. Null until the fetch completes or when
      // there's no store id yet (new, unsaved store); a fetch failure
      // leaves it null too (fails toward the old unconditional copy rather
      // than a false "broken" warning).
      shippingReadiness: null,
    };
  },

  computed: {
    ...mapGetters(["getPredefinedCheckoutFields"]),

    /**
     * Locks the Checkout Info form ONLY when the campaign is complete
     * or a new order was just placed in this session. Drops both
     * ``has_orders`` and ``active`` from the gate -- admins can edit
     * checkout fields even after orders exist or the campaign is
     * live, because the backend snapshots derived amounts at order
     * time and the platform applies current rules to historical data
     * correctly (CURRENT-RULE pattern). See ./disableFlags.js for
     * full rationale.
     */
    disableForCampaignLifecycle() {
      return computeDisableFlags({
        campaign: this.campaign,
        isNewOrderAdded: this.$store.getters.getIsNewOrderAdded,
      }).disableForCampaignLifecycle;
    },

    tabErrorMessage() {
      if (
        this.$store.getters.getCollectSalesTaxEnabled ||
        this.$store.getters.getSmartShippingEnabled
      ) {
        return "One or more location fields must be enabled when sales tax or smart shipping is enabled";
      } else if (
        (this.fieldCustomEnabled &&
          this.fieldCustom.length == 0 &&
          this.customDropdowns.length == 0) ||
        (!this.customFieldsValid && this.fieldCustom.length != 0) ||
        (!this.customDropdownsValid && this.customDropdowns.length != 0)
      ) {
        return "Custom checkout fields incomplete";
      } else {
        return "One or more fields must be enabled";
      }
    },

    indicateLocationFields() {
      if (
        this.$store.getters.getSmartShippingEnabled ||
        this.$store.getters.getCollectSalesTaxEnabled
      ) {
        if (
          !this.checkoutFields.includes("Shopper Location") &&
          !this.checkoutFields.includes("Location")
        ) {
          return true;
        }
      } else {
        return false;
      }
    },

    /**
     * Merged campaign-shaped object handed to CheckoutPreviewPane.
     * The preview pane is pure-presentational and reads the persisted
     * column names directly; here we project the in-component field
     * flags (which reflect uncommitted toggles) on top of the
     * persisted campaign so the right-side preview updates the
     * moment a switch flips -- without waiting for save.
     *
     * shipping_mode is mapped from the ``shipMode`` value (``ship_to_*``)
     * to the canonical preview-pane vocabulary (Phase 3:
     * ``shopper_address|company``).
     */
    previewCampaign() {
      return {
        ...(this.campaign || {}),
        field_first_name_enabled: this.fieldFirstNameEnabled,
        field_last_name_enabled: this.fieldLastNameEnabled,
        field_company_email_enabled: this.fieldCompanyEmailEnabled,
        field_phone_enabled: this.fieldPhoneEnabled,
        shipping_mode:
          SHIP_MODE_TO_SHIPPING_MODE[this.shipMode] || "shopper_address",
        ship_to_department_name: this.shipToDepartmentName,
        ask_department:
          this.departmentsInUse && this.shipMode !== "ship_to_department",
        checkout_fields: this.fieldCustomEnabled
          ? this.fieldCustom.slice()
          : [],
        dropdown_fields: this.fieldCustomEnabled
          ? this.customDropdowns.slice()
          : [],
      };
    },

    /**
     * True when the store's client uses departments. Drives the
     * department-shipping note (the honest replacement for the dead
     * Department switch): orders ship to the shopper's department address,
     * falling back to company. Department behavior is driven by the
     * client_departments mapping + the Phase 2 resolver, not a toggle.
     */
    departmentsInUse() {
      // Feature gate first: with Departments OFF the distributor has no
      // surface to view or manage them, so this store must not claim to
      // ship by department nor silently persist a "Department" checkout
      // field whose storefront picker is hidden. Mirrors the backend,
      // which serializes ``client_departments: []`` when the flag is OFF
      // -- this check keeps the editor honest even when it holds a
      // campaign payload fetched before the flag flipped.
      if (!this.enableDepartments) return false;
      const depts = this.campaign && this.campaign.client_departments;
      return Array.isArray(depts) && depts.length > 0;
    },

    /** The saved store's id (null for a brand-new, unsaved store). */
    storeId() {
      const c = this.campaign || {};
      return (c._jv && c._jv.id) || c.id || null;
    },

    /** The store's departments (the storefront picker's set). */
    storeDepartments() {
      const depts = this.campaign && this.campaign.client_departments;
      return Array.isArray(depts) ? depts : [];
    },

    /**
     * Department-mode picker options: the store's departments, each
     * labelled with whether it has a validated address (one without ships
     * to the store address instead). The status is unknown until the
     * client's department list loads; the bare name is shown meanwhile.
     */
    departmentPickerItems() {
      return this.storeDepartments.map((d) => {
        const status = this.departmentAddressStatus[d.id];
        let title = d.name;
        if (status === true) title = `${d.name} -- address validated`;
        else if (status === false) {
          title = `${d.name} -- no validated address (ships to the store address)`;
        }
        return { title, value: d.id };
      });
    },

    shipToDepartmentName() {
      if (this.shipMode !== "ship_to_department") return null;
      const dept = this.storeDepartments.find(
        (d) => d.id === this.shipToDepartmentId
      );
      return dept ? dept.name : null;
    },

    storeAddressFallbackHint() {
      const chain =
        "If this store has no validated ship-to address, orders ship to the " +
        "client's company address, and if the client has none, to the " +
        "distributor address.";
      if (this.shipMode === "ship_to_department") {
        return (
          "Orders ship to the chosen department's address. If it has no " +
          "validated address, they ship to this store address. " +
          chain
        );
      }
      if (this.departmentsInUse) {
        return (
          "Orders ship to the address of the department the shopper picks. " +
          "If it has no validated address, they ship to this store address. " +
          chain
        );
      }
      return chain;
    },

    departmentRequiredReason() {
      if (this.shipMode === "ship_to_company") {
        return (
          "Shoppers must pick their department at checkout: the order is " +
          "charged to it and ships to that department's address (or to the " +
          "store address if the department has none)."
        );
      }
      return (
        "Shoppers must pick their department at checkout so the order is " +
        "charged to it. The order ships to the address they enter."
      );
    },

    departmentShippingNote() {
      if (this.shipMode === "ship_to_shopper_address") {
        return (
          "This store uses departments. Orders ship to the address the " +
          "shopper enters; their department decides the budget the order " +
          "is charged to."
        );
      }
      return (
        "This store uses departments. Orders ship to the department's " +
        "address, falling back to the store address and then the company " +
        "address when the department has none."
      );
    },

    /**
     * Router link to the client detail page (edited inline) (Phase 1 editors for the
     * company + department addresses). Null when the store has no client
     * yet -- the read-only block then prompts to set one.
     */
    clientPageLink() {
      const cid = this.campaign && this.campaign.client_id;
      if (cid == null) return null;
      return { name: "client_detail", params: { id: String(cid) } };
    },

    /**
     * Whether the store's shipping fallback chain actually resolves, per
     * the SAME readiness payload the Review & Launch card gates launch on
     * (GET /api/stores/<id>/readiness/, blocker code
     * "shipping_unresolvable"). true | false once fetched; null while
     * unknown (not yet fetched, no store id, or the fetch failed) --
     * null intentionally renders the pre-existing unconditional copy
     * rather than a possibly-wrong warning.
     *
     * Card trello.com/c/NCJnYsw5: reading the backend's own gate signal
     * here (rather than re-deriving "is this resolvable?" in the
     * frontend) is what keeps this note and the readiness card from
     * disagreeing again.
     */
    shippingResolvable() {
      const blockers = this.shippingReadiness && this.shippingReadiness.blockers;
      if (!Array.isArray(blockers)) return null;
      const unresolvable = blockers.some(
        (b) => b && b.code === SHIPPING_UNRESOLVABLE_CODE
      );
      return !unresolvable;
    },

    /**
     * The backend's own explanation of why shipping is unresolvable
     * (state_transition_guard.py::SHIPPING_UNRESOLVABLE_COPY) -- names the
     * real reason (e.g. an existing company/department address that isn't
     * validated) rather than a frontend-invented message, so this surface
     * never drifts from the Review & Launch blocker text. Falls back to a
     * generic pointer if the readiness fetch somehow returned the
     * unresolvable code without a message.
     */
    shippingUnresolvableMessage() {
      const blockers = this.shippingReadiness && this.shippingReadiness.blockers;
      if (!Array.isArray(blockers)) return "";
      const blocker = blockers.find(
        (b) => b && b.code === SHIPPING_UNRESOLVABLE_CODE
      );
      return (
        (blocker && blocker.message) ||
        "This store's shipping address can't be resolved. Check Review & " +
          "Launch for details."
      );
    },

    /**
     * Human-readable, ordered lines of the company shipping address for the
     * read-only display. Empty array when no company address is set (the
     * block then renders the "no address set" prompt). Reads the fetched
     * ``companyAddress`` mirror.
     */
    companyAddressLines() {
      const a = this.companyAddress;
      if (!a) return [];
      const lines = [];
      if (a.ship_to_recipient_name) lines.push(a.ship_to_recipient_name);
      if (a.ship_to_address1) lines.push(a.ship_to_address1);
      if (a.ship_to_address2) lines.push(a.ship_to_address2);
      const cityStateZip = [
        a.ship_to_city,
        a.ship_to_state,
      ]
        .filter(Boolean)
        .join(", ");
      const withZip = [cityStateZip, a.ship_to_postal_code]
        .filter(Boolean)
        .join(" ");
      if (withZip) lines.push(withZip);
      if (a.ship_to_country) lines.push(a.ship_to_country);
      return lines;
    },

    checkoutFields() {
      let fields = [];

      this.fieldFirstNameEnabled && fields.push("First Name");
      this.fieldLastNameEnabled && fields.push("Last Name");
      this.fieldCompanyEmailEnabled && fields.push("Company Email");
      this.fieldDepartmentEnabled && fields.push("Department");
      this.fieldLocationEnabled && fields.push("Location");
      this.fieldManagerEnabled && fields.push("Manager");
      this.fieldShopperLocationEnabled && fields.push("Shopper Location");

      // Phone lives on its own boolean column (field_phone_enabled),
      // NOT in the checkout_fields JSON list. Older surfaces that
      // gate behavior on checkout_fields.includes("Phone") see it
      // here so the storefront cart can render the Phone input.
      this.fieldPhoneEnabled && fields.push("Phone");

      if (this.fieldCustomEnabled) {
        fields = fields.concat(this.fieldCustom);
      }

      return fields;
    },

    valid() {
      const valid = true;
      // These are used as reactive dependencies to force recomputation.
      // Use explicit property access rather than bare array references
      // to avoid Vue 3 Proxy tracking issues with mutable arrays.
      void this.validateKey;
      void this.fieldCustomEnabled;
      void this.fieldCustom.length;
      void this.customDropdowns.length;
      // No "sales tax / smart shipping needs a location field" rule (Bug
      // Intake #355): ship-to-company mode writes no location field -- its
      // ship-to IS the company address, which sales tax is computed from --
      // so the rule flagged every such store's complete Checkout, and the
      // stored flag hid its Open/Closed toggle. Whether the store has a
      // destination is the server's ship-to readiness check
      // (store_section_readiness), which this section's verdict must match.
      if (this.checkoutFields.length == 0 && !this.fieldCustomEnabled) {
        return false;
      }

      if (
        (this.fieldCustomEnabled &&
          this.fieldCustom.length == 0 &&
          this.customDropdowns.length == 0) ||
        (!this.customFieldsValid && this.fieldCustom.length != 0) ||
        (!this.customDropdownsValid && this.customDropdowns.length != 0)
      ) {
        return false;
      }

      return valid;
    },
  },

  mounted() {
    document.addEventListener("keydown", this.handlePreviewEscape);
    this.$nextTick(() => {
      if (this.$refs.form && typeof this.$refs.form.validate === "function") {
        this.$refs.form.validate();
      }
    });
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this.handlePreviewEscape);
  },

  watch: {
    campaign: {
      immediate: true,
      handler(newVal, oldVal) {
        // Re-run fillPreliminaryData when campaign data arrives in edit mode
        if (this.editMode && !this._hasInitialized && newVal?._jv?.id) {
          this._hasInitialized = true;
          this.fillPreliminaryData();
        }
        // this field should be enabled if one of the selected features is to email shoppers a
        // confirmation email. if we don't collect their emails, how can we email them?
        //
        // PR-G (2026-06-22): only HYDRATE from an existing
        // checkout_fields list when the store actually has one.
        // Brand-new stores arrive with checkout_fields: [] -- the empty
        // array would historically force the toggle OFF here, defeating
        // the new "Email defaults ON" rule (default lives in data() +
        // fillPreliminaryData). The hydration path only fires when the
        // persisted list has at least one entry.
        if (newVal?.email_shopper) {
          this.fieldCompanyEmailEnabled = true;
          this.$store.commit("setCompanyEmailEnabled", true);
        } else if (
          newVal?.checkout_fields &&
          newVal.checkout_fields.length > 0
        ) {
          if (newVal.checkout_fields.indexOf("Company Email") === -1) {
            this.fieldCompanyEmailEnabled = false;
            this.$store.commit("setCompanyEmailEnabled", false);
          } else {
            this.fieldCompanyEmailEnabled = true;
            this.$store.commit("setCompanyEmailEnabled", true);
          }
        }

        // Auto-enable Department field when departments are configured.
        // Pre-C2 the toggle stayed off-but-disabled, leaving routing
        // ambiguous; the C2 rule is "departments configured -> field
        // ON and read-only."
        // Routed through ``departmentsInUse`` (not the raw array) so the
        // feature gate applies here too.
        if (this.departmentsInUse && !this.fieldDepartmentEnabled) {
          this.fieldDepartmentEnabled = true;
        }

        // Re-derive shipMode + Phone from campaign on prop change so
        // jsonapi-vuex hydration after the initial mount lands in the
        // UI without requiring a manual reload.
        if (newVal && typeof newVal.field_phone_enabled === "boolean") {
          this.fieldPhoneEnabled = newVal.field_phone_enabled;
        }

        // Card trello.com/c/NCJnYsw5: fetch the same readiness payload
        // Review & Launch reads, once the store id is available, so the
        // department/company shipping note can reflect real resolvability.
        // Guarded on the store id itself (not ``_hasInitialized``, which
        // is scoped to ``fillPreliminaryData``/editMode) -- re-fetch when
        // the id changes (e.g. a brand-new store gets its first save).
        const storeId = newVal?._jv?.id;
        if (storeId && storeId !== this._readinessFetchedForStoreId) {
          this._readinessFetchedForStoreId = storeId;
          this.loadShippingReadiness(storeId);
        }
      },
    },

    // The feature flag arrives ASYNCHRONOUSLY (one shared GET), while
    // hydration runs synchronously on mount against the fail-safe default
    // of TRUE. Without this watcher a store whose persisted
    // ``checkout_fields`` contains "Department" would hydrate the field ON
    // and then never re-derive once the real value (OFF) landed, leaving a
    // department requirement on a store whose picker is hidden. Re-deriving
    // on the flag itself is what makes the gate hold in the real async
    // ordering rather than only in a test that resolves the flag first.
    enableDepartments: function(newVal) {
      if (newVal === false) {
        this.fieldDepartmentEnabled = false;
      } else if (this.departmentsInUse) {
        // Flag flipped back ON (or resolved TRUE after a transient error):
        // restore the auto-enable for a store that does use departments.
        this.fieldDepartmentEnabled = true;
      }
    },

    fieldDepartmentEnabled: function(newVal, oldVal) {
      if (newVal === true) {
        this.$emit("set-department-enabled", true);
      } else {
        this.$emit("set-department-enabled", false);
      }
    },

    valid: function(newVal, oldVal) {
      if (!this._hasInitialized) return;
      this.$emit("update-tab-invalid", "checkoutInfo", !newVal);
      this.$emit("checkout-fields-validate", newVal);
      this.$emit("campaign-changed");
    },

    shipMode: function(newVal, oldVal) {
      // Project the radio choice onto the legacy ship flags so
      // checkoutFields + downstream computations stay correct without
      // a second source of truth.
      this.syncShipFlagsFromMode(newVal);
      // Phase 3: the locations mode is retired; neither remaining mode
      // participates in locations. Keep emitting false so any parent
      // still listening hides the (dead) location editor.
      this.$emit("set-location-enabled", false);
      // Lazily fetch the company address for the read-only display.
      if (newVal === "ship_to_company" || newVal === "ship_to_department") {
        this.loadCompanyAddress();
      }
      if (newVal === "ship_to_department") {
        this.loadDepartmentAddressStatus();
      }
      this.clientAddressMissing = false;
    },

    fieldManagerEnabled: function(newVal, oldVal) {
      if (newVal === true) {
        this.$emit("set-manager-enabled", true);
      } else {
        this.$emit("set-manager-enabled", false);
      }
    },

    // NOTE: checkoutFields watcher REMOVED -- it caused an infinite reactivity loop
    // in Vue 3. The computed returns a new array reference on every evaluation,
    // triggering the watcher endlessly. campaign-changed is emitted from individual
    // field toggle watchers and the validateTab calls instead.
  },

  methods: {
    openPreview() {
      this.previewOpen = true;
      // Move focus into the dialog so keyboard users land on the close control.
      this.$nextTick(() => {
        const btn = this.$refs.previewCloseBtn;
        if (btn && typeof btn.focus === "function") btn.focus();
      });
    },
    closePreview() {
      this.previewOpen = false;
      // Hand focus back to the control that opened the popover.
      this.$nextTick(() => {
        const btn = this.$refs.previewOpenBtn;
        if (btn && typeof btn.focus === "function") btn.focus();
      });
    },
    handlePreviewEscape(e) {
      if (e.key === "Escape" && this.previewOpen) this.closePreview();
    },
    changeCustomCheckoutFieldDropdown() {
      this.usedCustomDropdownOptions.forEach((dropdown, index) => {
        const options = dropdown.options;
        for (const option in options) {
          if (Object.hasOwnProperty.call(options, option)) {
            const isOrdered = options[option];
            if (
              this.customDropdowns[index].options.indexOf(option) === -1 &&
              isOrdered
            ) {
              this.customDropdowns[index].options.push(option);
              this.customDropdowns[index].options.sort();
            }
          }
        }
      });
    },

    customDropdownOptionRules(options) {
      const rules = [];
      rules.push(options.length != 0 || "Required");

      return rules;
    },

    optionHasOrders(item) {
      // return if usedCustomDropdownOptions has key in the options key of item and value of true
      return this.usedCustomDropdownOptions.some((dropdown) => {
        return dropdown.options[item] === true;
      });
    },

    async getUsedCustomDropdownOptions() {
      let result;
      try {
        result = await this.$axios.get(
          `/campaign/getUsedCustomDropdownOptions/${this.campaign.id}`
        );
        this.usedCustomDropdownOptions = result.data;
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      }
    },

    changeCustomCheckoutField() {
      this.$emit("campaign-changed");
      this.validateTab();
    },

    addCustomDropdown() {
      this.customDropdowns.push({ name: "", options: [] });
      this.validateTab();
    },

    removeCustomDropdownField(index) {
      this.customDropdowns.splice(index, 1);
    },

    removeCustomDropdownFieldOption(fieldIndex, option) {
      this.customDropdowns[fieldIndex].options.splice(
        this.customDropdowns[fieldIndex].options.indexOf(option),
        1
      );
    },

    loadComponent() {
      // Do NOT add `this.updateCampaign(this.campaign)` here. On
      // client-side navigation, loadComponent runs in a $nextTick
      // scheduled BEFORE children's loadFromCampaign fires. Calling
      // updateCampaign at that point would read child defaults from
      // getData() and Object.assign them onto the campaign prop,
      // clobbering persisted values. See StoreFeatures.loadComponent
      // for the bug we fixed, and spec
      // store-features-load-component-no-clobber.spec.js for the
      // regression guard.
      this.closedErrorNotification = false;
    },
    getRules(field) {},

    validateTab() {
      this.validateKey++;
      if (this.$refs.form && typeof this.$refs.form.validate === "function") {
        this.$refs.form.validate();
      }
      if (this.fieldCustomEnabled) {
        if (this.$refs.customCheckoutForm) {
          this.$refs.customCheckoutForm.validate();
        }
        if (this.$refs.customDropdownCheckoutForm) {
          this.$refs.customDropdownCheckoutForm.validate();
        }
      }

      return !this.valid;
    },

    /**
     * Project shipMode onto the legacy ship flags so the storefront
     * (which reads the checkout_fields JSON list) stays correct. Called
     * from the shipMode watcher and once from fillPreliminaryData.
     *
     * Phase 3: only two modes remain. Collect-at-checkout writes
     * "Shopper Location"; company mode writes NONE of the legacy ship
     * flags -- the Phase 2 resolver falls through to the client's
     * company/department address when no shopper address is collected.
     * The retired Location / Manager flags are never written.
     */
    syncShipFlagsFromMode(mode) {
      this.fieldShopperLocationEnabled = mode === "ship_to_shopper_address";
      this.fieldLocationEnabled = false;
      this.fieldManagerEnabled = false;
    },

    onShipModeChange(newVal) {
      // Vuetify radio fires this on user interaction; the watcher
      // also handles programmatic mutations. Emit campaign-changed
      // explicitly so autosave kicks in on user clicks even when the
      // valid computed doesn't change.
      this.checkoutSaveRefusal = "";
      if (newVal === "ship_to_company") {
        this.loadCompanyAddress();
      }
      this.$emit("campaign-changed");
    },

    onShipDepartmentChange() {
      this.checkoutSaveRefusal = "";
      this.$emit("campaign-changed");
    },

    /**
     * The server REFUSED a store save over this section's slice, so nothing
     * was written (Bug Intake #450). The parent has put the SAVED
     * checkout_fields / shipping_mode / ship_to_department_id / Phone /
     * dropdowns back on ``campaign``; re-hydrate every control from them so
     * the screen never shows a setting the server rejected, and say why.
     * Called by StoreAddEdit.revertToSavedCheckout.
     */
    adoptSavedCheckout(reason) {
      this.fillPreliminaryData();
      this.checkoutSaveRefusal = reason || "";
    },

    /**
     * "Use the client's company address": copy the client's company
     * address INTO the store address form. It only fills the form -- the
     * user saves through the editor's validated path, which is the only
     * way the store address gets its validation stamp.
     */
    async useClientCompanyAddress() {
      const cid = this.campaign && this.campaign.client_id;
      if (cid == null || this.copyingClientAddress) return;
      this.copyingClientAddress = true;
      this.clientAddressMissing = false;
      try {
        const res = await api.get(`/clients/${cid}/shipping-address`);
        const addr = res && res.data && res.data.shipping_address;
        if (addr && (addr.address1 || "").trim()) {
          const editor = this.$refs.storeAddressEditor;
          if (editor && typeof editor.fillFrom === "function") {
            editor.fillFrom(addr);
          }
        } else {
          this.clientAddressMissing = true;
        }
      } catch (err) {
        this.clientAddressMissing = true;
      } finally {
        this.copyingClientAddress = false;
      }
    },

    /**
     * Address status of the client's departments, for the department
     * picker labels. Non-fatal: on failure the picker shows bare names.
     */
    async loadDepartmentAddressStatus() {
      const cid = this.campaign && this.campaign.client_id;
      if (cid == null || !this.departmentsInUse) return;
      try {
        const res = await api.get(`/clients/${cid}/departments/`);
        const rows = (res && res.data && res.data.departments) || [];
        const status = {};
        rows.forEach((d) => {
          if (d && d.id != null) status[d.id] = !!d.shipping_address_validated;
        });
        this.departmentAddressStatus = status;
      } catch (err) {
        this.departmentAddressStatus = {};
      }
    },

    /**
     * Fetch the store's launch-readiness payload (the SAME
     * ``GET /api/stores/<id>/readiness/`` call the Review & Launch card
     * makes via ``useStoreReadinessStore``) so the shipping note can key
     * off the real "shipping_unresolvable" signal instead of asserting
     * the fallback chain works. Non-fatal: a fetch failure leaves
     * ``shippingReadiness`` at its previous value (null on first load),
     * which renders the pre-existing unconditional copy -- we never want
     * a network hiccup here to falsely claim the store is broken.
     *
     * Card trello.com/c/NCJnYsw5.
     */
    async loadShippingReadiness(storeId) {
      try {
        const readinessStore = useStoreReadinessStore();
        const data = await readinessStore.fetchReadiness(storeId);
        this.shippingReadiness = data;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("loadShippingReadiness failed", describeError(err));
      }
    },

    /**
     * Fetch the client's company shipping address (read-only mirror) so
     * the "Ship to company address" block can display it. No-op when the
     * store has no client or the address is already loaded. Failures are
     * swallowed to a null address -- the block then shows the "no address
     * set" prompt rather than crashing the editor.
     */
    async loadCompanyAddress() {
      const cid = this.campaign && this.campaign.client_id;
      if (cid == null) return;
      if (this.companyAddress && this.companyAddress._clientId === cid) return;
      try {
        const res = await api.get(`/clients/${cid}`);
        const attrs =
          (res && res.data && res.data.data && res.data.data.attributes) ||
          (res && res.data && res.data.attributes) ||
          (res && res.data) ||
          {};
        this.companyAddress = { ...attrs, _clientId: cid };
      } catch (err) {
        this.companyAddress = null;
      }
    },

    fillPreliminaryData() {
      const existingFields = this.$props.campaign.checkout_fields;
      // Rebuilt from the saved list below; starting empty is what lets
      // adoptSavedCheckout re-run this without doubling the custom fields.
      this.fieldCustom = [];

      // Default required fields for new campaigns (empty checkout_fields)
      if (!existingFields || existingFields.length === 0) {
        this.fieldFirstNameEnabled = true;
        this.fieldLastNameEnabled = true;
        // PR-G (2026-06-22): Email defaults to ON for new stores so the
        // Identity card lights up consistently and Owners do not have to
        // opt back in to receive shopper emails.
        this.fieldCompanyEmailEnabled = true;
        this.fieldShopperLocationEnabled = true;
        this.shipMode = "ship_to_shopper_address";
        // Phone defaults to the persisted column value (false on a
        // brand-new store) -- already initialized in data().
        if (typeof this.$props.campaign.field_phone_enabled === "boolean") {
          this.fieldPhoneEnabled = this.$props.campaign.field_phone_enabled;
        }
        return;
      }

      this.fieldFirstNameEnabled = !!~existingFields.indexOf("First Name");
      this.fieldLastNameEnabled = !!~existingFields.indexOf("Last Name");
      this.fieldCompanyEmailEnabled = !!~existingFields.indexOf(
        "Company Email"
      );
      this.$store.commit(
        "setCompanyEmailEnabled",
        this.fieldCompanyEmailEnabled
      );
      // Feature gate: a store configured while Departments was ON carries
      // "Department" in its persisted ``checkout_fields``. With the flag
      // OFF the storefront picker that would satisfy that field is hidden,
      // so rehydrating it would keep re-saving a requirement no shopper
      // can meet. Hydrate it only while the feature is on; the persisted
      // JSON is left untouched until the distributor next saves, so
      // flipping the flag back ON restores the field.
      this.fieldDepartmentEnabled =
        this.enableDepartments && !!~existingFields.indexOf("Department");
      this.fieldLocationEnabled = !!~existingFields.indexOf("Location");
      this.fieldManagerEnabled = !!~existingFields.indexOf("Manager");
      this.fieldShopperLocationEnabled = !!~existingFields.indexOf(
        "Shopper Location"
      );

      // Phone reads from the persisted boolean column, NOT from the
      // JSON list. (Sandbox seeds may or may not have "Phone" in the
      // list; the column is the source of truth.)
      if (typeof this.$props.campaign.field_phone_enabled === "boolean") {
        this.fieldPhoneEnabled = this.$props.campaign.field_phone_enabled;
      } else {
        this.fieldPhoneEnabled = false;
      }

      // Department auto-enable: if departments are configured on the
      // campaign, force the toggle ON even when the persisted JSON
      // list omits it. The toggle renders read-only in this state.
      // Routed through ``departmentsInUse`` so the feature gate applies on
      // hydration as well as on prop change.
      if (this.departmentsInUse) {
        this.fieldDepartmentEnabled = true;
      }

      // Derive shipMode from the three legacy flags. If multiple are
      // set (legacy corruption), pick leftmost (shopper) per
      // ``deriveShipMode``.
      this.shipMode = deriveShipMode({
        shopper: this.fieldShopperLocationEnabled,
        location: this.fieldLocationEnabled,
        manager: this.fieldManagerEnabled,
        // existingFields is non-empty here (the empty case returned above).
        saved: true,
      });
      // The persisted ``shipping_mode`` (migration store_ship_to_001) is
      // canonical; the flag derivation above only covers a payload that
      // predates it.
      this.hydrateShipModeFromCampaign();
      // Project back so the three legacy flags are coherent with the
      // chosen radio (drops any stray extra flags from corrupt rows).
      this.syncShipFlagsFromMode(this.shipMode);

      existingFields.forEach((property) => {
        if (this.getPredefinedCheckoutFields.indexOf(property) === -1) {
          this.fieldCustom.push(property);
        }
      });
      this.customDropdowns = this.$props.campaign.dropdown_fields
        ? this.$props.campaign.dropdown_fields
        : [];

      this.fieldCustomEnabled =
        (this.fieldCustom && this.fieldCustom.length > 0) ||
        (this.campaign.dropdown_fields &&
          this.campaign.dropdown_fields.length > 0);
      this.$store.commit("setFieldCustomEnabled", this.fieldCustomEnabled);
      this.$emit("checkout-fields-validate", this.valid);

      this.$emit(
        "set-location-enabled",
        this.campaign.locations && this.campaign.locations.length > 0
      );
      this.$emit("set-department-enabled", this.fieldDepartmentEnabled);
      this.$emit("set-manager-enabled", this.fieldManagerEnabled);

      this.$emit("update-tab-invalid", "checkoutInfo", !this.valid);
    },

    hydrateShipModeFromCampaign() {
      const c = this.campaign || {};
      const mode = SHIPPING_MODE_TO_SHIP_MODE[c.shipping_mode];
      if (mode) this.shipMode = mode;
      this.shipToDepartmentId =
        c.ship_to_department_id == null ? null : c.ship_to_department_id;
      // Load explicitly: hydration runs inside the IMMEDIATE ``campaign``
      // watcher, before the ``shipMode`` watcher exists, so that watcher
      // never sees this change.
      if (this.shipMode === "ship_to_department") {
        this.loadCompanyAddress();
        this.loadDepartmentAddressStatus();
      } else if (this.shipMode === "ship_to_company") {
        this.loadCompanyAddress();
      }
    },

    addCustomField() {
      this.fieldCustom.push("");
    },

    removeCustomField(index) {
      this.fieldCustom.splice(index, 1);
      this.validateTab();
    },

    updateCampaign(campaign) {
      // An incomplete Checkout section is a NOT-READY section, not a
      // failed save. The parent autosave (StoreAddEdit._runAutoSave)
      // iterates EVERY mounted section and re-throws on campaign.error,
      // so stamping one here aborted the whole-store save -- including
      // saves triggered from an unrelated section. A distributor who had
      // switched "Custom fields" on without adding one yet then lost
      // every subsequent edit (e.g. adding a product) to a "Save failed"
      // top bar, with only a console line to explain it.
      //
      // Readiness is still reported to the parent via update-tab-invalid,
      // which drives the section status chip and the Review & Launch
      // gate -- that gate, not the autosave, is what blocks going live.
      // Same defect class as the sibling Login section fix; see
      // __tests__/store-shopper-save-failed.spec.js and
      // __tests__/checkout-autosave-does-not-abort-save.spec.js.
      campaign.error = null;
      if (!this.valid) {
        this.$emit("update-tab-invalid", "checkoutInfo", !this.valid);
      }

      if (campaign.email_shopper) {
        this.fieldCompanyEmailEnabled = true;
        this.$store.commit("setCompanyEmailEnabled", true);
      }
      campaign.checkout_fields = this.checkoutFields;
      // The canonical shipping mode + department. The server keeps
      // "Shopper Location" in step with the mode, so the two cannot drift.
      campaign.shipping_mode =
        SHIP_MODE_TO_SHIPPING_MODE[this.shipMode] || "shopper_address";
      campaign.ship_to_department_id =
        this.shipMode === "ship_to_department" ? this.shipToDepartmentId : null;
      // Persist the Phone toggle to the boolean column. Always write
      // even when false so the API allowlist sees an explicit value
      // (avoids accidentally inheriting a stale row value when the
      // user disables Phone).
      campaign.field_phone_enabled = !!this.fieldPhoneEnabled;
      if (this.fieldCustomEnabled) {
        campaign.dropdown_fields = this.customDropdowns;
      } else {
        campaign.dropdown_fields = [];
      }
      return campaign;
    },

    changeCompanyEmailEnabled(val) {
      this.fieldCompanyEmailEnabled = val;
      this.$store.commit("setCompanyEmailEnabled", val);
      this.campaign.checkout_fields = this.checkoutFields;
      this.$emit("campaign-changed");
    },
    changePhoneEnabled(val) {
      // Mirror the email handler so autosave fires on user toggle.
      this.fieldPhoneEnabled = !!val;
      this.$emit("campaign-changed");
    },
    closeTabErrorNotifiation() {
      this.closedErrorNotification = true;
    },
  },
};
</script>

<style scoped>
/* G2 (task #110): force horizontal layout for the Identity card's
 * v-switch labels.
 *
 * Real-fix iteration (2026-06-17): the previous .identity-row v-row +
 * 4-up grid let each col collapse to ~90px in the two-column store-
 * editor layout, and the .v-label :deep selector didn't actually
 * match because Vuetify 3's v-switch renders its label inside
 * .v-selection-control__label (not .v-label). Labels were still
 * wrapping letter-by-letter on 8083.
 *
 * Fix: identity-row is now a flex-wrap container with auto-width
 * children. Each v-switch takes its intrinsic width (control +
 * full label on one line). When horizontal space runs out, WHOLE
 * toggles wrap to a new line -- never mid-label.
 *
 * We retain ``white-space: nowrap`` on the underlying
 * .v-selection-control__label as a belt-and-suspenders guard in
 * case a future change re-introduces a width constraint on the
 * switch itself.
 */
.identity-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  white-space: nowrap;
}
.identity-row__switch {
  flex: 0 0 auto;
}
.identity-row :deep(.v-selection-control__wrapper) {
  flex: 0 0 auto;
}
.identity-row :deep(.v-selection-control__label),
.identity-row :deep(.v-label) {
  white-space: nowrap;
}

.checkout-blockquote {
  text-align: left !important;
  font-size: 18px;
  font-weight: bold;
  margin-left: 24px;
}
.ct-checkout-department-required-note {
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  color: var(--tf-fg-4, #6b7280);
  margin: -4px 0 0 56px;
}
.ship-aux-fields {
  margin-top: 12px;
}

/* Card trello.com/c/NCJnYsw5: warning styling for the department note
 * when the fallback chain doesn't resolve, and for the standalone
 * shipping-unresolvable warning block. Routed through the shared
 * --tf-warning-* tokens (same amber "informational/caution" palette as
 * StoreReadinessCard's --informational state and OrderDetailView's
 * warning banner). */
.ship-dept-block__note--warning {
  color: var(--tf-warning-dark, #8a6d3b);
  font-weight: 600;
}
.ship-unresolvable-warning {
  margin-top: 12px;
  padding: 10px 14px;
  border: 1px solid var(--tf-warning, #fcb900);
  border-radius: 8px;
  background: var(--tf-warning-soft, #fff8e1);
}
.ship-unresolvable-warning__note {
  margin: 0;
  font-size: 13px;
  color: var(--tf-warning-dark, #8a6d3b);
}

/* Checkout preview is a NON-MODAL popover, not a column: the form uses the
 * full width and stays editable (no scrim) so the preview can update live.
 * Drawer geometry/tokens mirror the Storefront preview drawer in
 * StoreEditShell.vue (right slide-in), narrower because the content is a
 * form mock rather than a full storefront. */
.checkout-fields-layout {
  display: block;
}
.checkout-preview-toolbar {
  position: sticky;
  /* Below the editor's sticky pager row (StoreEditShell sets --ces-pager-h). */
  top: var(--ces-pager-h, 0px);
  z-index: 5;
  display: flex;
  justify-content: flex-end;
  padding: 8px 0;
  margin-bottom: 8px;
  /* Transparent so the page background shows; only the button takes clicks,
   * so the strip never blocks the content scrolling beneath it. */
  pointer-events: none;
}
.checkout-preview-open-btn {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 9999px;
  background: var(--tf-primary);
  color: var(--tf-bg);
  font: 600 14px var(--tf-font-sans, 'Roboto', sans-serif);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(19, 55, 82, 0.18);
}
.checkout-preview-open-btn:hover { filter: brightness(1.08); }
.checkout-preview-open-btn:focus-visible {
  outline: 2px solid var(--tf-secondary);
  outline-offset: 2px;
}
.checkout-preview-open-btn .tf-icon { font-size: 18px; }
.checkout-preview-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(480px, 100vw);
  background: var(--tf-bg);
  z-index: 91;
  box-shadow: -12px 0 40px rgba(19, 55, 82, 0.18);
  display: flex;
  flex-direction: column;
}
.checkout-preview-drawer__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--tf-border);
  background: var(--tf-surface-muted);
}
.checkout-preview-drawer__ttl {
  font: 500 14px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-secondary);
}
.checkout-preview-drawer__meta {
  font: 400 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4);
}
.checkout-preview-drawer__close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tf-fg-4);
  cursor: pointer;
}
.checkout-preview-drawer__close:hover {
  background: var(--tf-surface-dark);
  color: var(--tf-secondary);
}
.checkout-preview-drawer__close:focus-visible {
  outline: 2px solid var(--tf-primary);
}
.checkout-preview-drawer__body {
  flex: 1;
  overflow: auto;
  padding: 20px;
}
</style>
